import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import Loading from '@/packages/loading' // 顶部路由加载进度条
import $auth from '@/utils/auth'

import { isWeChat, usewxconfig } from '@/utils'
import userRouter from './user'
import adminRouter from './admin'

const envCfg = window.g || {}
const HOST = envCfg.baseURL + '/ecoSystem'
//账户中心
Vue.use(VueRouter)

//路由页面切换的时候，保持页面在滚动顶部显示
const scrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    return savedPosition
  } else {
    const position = {}
    if (to.hash) {
      position.selector = to.hash
    }
    if (to.matched.some(m => m.meta.scrollToTop)) {
      position.x = 0
      position.y = 0
    }
    return position
  }
}

// 定义路由
const routes = [
  {
    // 首页
    path: '/',
    component: () => import('@/views/user/index/Home.vue'),
    meta: {
      layout: {
        pageWrap: false
      }
    }
  },
  {
    path: '/login',
    component: () => import('@/layout/Empty.vue'),
    meta: {
      layout: {
        name: 'empty',
        pageWrap: false
      }
    },
    children: [
      {
        path: '',
        component: () => import('@/views/login/index.vue')
      }
    ]
  },
  {
    // iframe 路由
    path: '/iframe/:url/:title',
    name: 'iframe',
    component: () => import('@/views/Iframe'),
    meta: {
      title(route) {
        return route.params.title
      },
      layout: {
        subPage: true,
        pageWrap: false
      }
    }
  },
  userRouter,
  adminRouter,
  {
    path: '*',
    redirect: '/'
  }
]

// 创建router实例
const router = new VueRouter({
  mode: 'history',
  base: __dirname,
  scrollBehavior,
  routes
})

/**
 * 查找路由路径中的元信息配置（matched 需倒序从末端查找） *
 * @param {Array} matched 路由路径（倒序）
 * @param {String} prop meta 属性
 * @returns 查找的值，未查到返回 undefined
 */
function getMatchedMeta(matched, prop) {
  const matchRoute = matched.find(item => prop in item.meta)
  return matchRoute && matchRoute.meta[prop]
}

/**
 * 获取免登地址
 */
function login() {
  window.location.href = HOST + '/api/v1/pre/getOAuth2Url'
}

/**
 * 路由守卫导航
 * meta 权限检测配置：
 * role: 'role-key' 角色权限
 * personVerify: true 个人实名
 * companyVerify: true 企业实名 （包含个人实名）
 * checkSign: true 印章授权
 * checkPackage: true 套餐余量
 */
router.beforeEach(async (to, from, next) => {
  // 开启全局页头进度
  Loading().stop()
  Loading().start()

  // 企微环境
  if (isWeChat() && !to.query.code && !sessionStorage.getItem('token')) {
    // 获取code
    login()
    return
  }

  if (isWeChat() && to.query.code) {
    // 免登
    let params = {
      code: to.query.code,
      type: 8,
      url: window.location.href
    }
    store.dispatch('checkOpenId', params).then(() => {
      store.dispatch('setUser')
      next('/')
    })
    return
  }

  // 未登录跳登录页
  if (!to.query.auth_code && !sessionStorage.getItem('token') && to.path !== '/login') {
    next('/login')
    return
  }
  // 免登
  if (to.query.auth_code && !sessionStorage.getItem('token') && to.path !== '/login') {
    let params = {
      code: to.query.auth_code,
      type: 5,
      url: window.location.href
    }
    store.dispatch('checkOpenId', params).then(() => {
      store.dispatch('setUser')
      next('/')
    })
    return
  }
  // 调用户信息
  if (!Object.keys(store.state.userInfo).length && to.path !== '/login') {
    await store.dispatch('setUser')
  }

  // 路由路径倒序
  const matched = [...to.matched].reverse()

  // 路由权限监测
  const role = getMatchedMeta(matched, 'role')

  if (role && !Vue.prototype.$hasRole(role)) {
    next({
      name: 'errorRole',
      query: {
        name: to.meta.title,
        url: to.fullPath
      }
    })
    return
  }

  // 检查个人实名认证
  if (getMatchedMeta(matched, 'personVerify') && !$auth.checkPerson()) {
    Loading().stop()
    next(from)
    return
  }

  // 检查企业实名认证
  if (getMatchedMeta(matched, 'companyVerify') && !$auth.checkCompany()) {
    Loading().stop()
    next(from)
    return
  }

  // 检查是否有印章授权
  // if (getMatchedMeta(matched, 'checkSign') && !$auth.checkSign()) {
  //   Loading().stop()
  //   next(from)
  //   return
  // }

  // 检测套餐余量
  if (getMatchedMeta(matched, 'checkPackage')) {
    try {
      await $auth.checkPackageAsync()
      next()
    } catch (e) {
      Loading().stop()
      next(from)
    }
    return
  }

  next()
})

// 路由进入后
router.afterEach(async (to, from) => {
  // 关闭全局页头进度
  Loading().stop()
  // Vue.nextTick(() => {
  //   window.sensors.quick('autoTrackSinglePage')
  // })

  const isUserPage = to.matched[0].path.indexOf('admin') === -1
  // 更新当前是否用户端页面
  store.commit('checkUserPage', isUserPage)

  // console.log('afterEach', to, location.href.split('#')[0], (location.origin + to.fullPath).split('#')[0])

  // 渲染通讯录组件: 内置浏览器
  if (isWeChat() && sessionStorage.getItem('token') && to.path !== '/login') {
    // 这里获取当前网页url竟然是上一个的，所以自己拼接：location.origin + to.fullPath
    usewxconfig((location.origin + to.fullPath).split('#')[0])
  }
})

// 页面离开后清理 name，避免当前页面被外部链接替换后无法切换到当前 name 页签
window.addEventListener('unload', () => {
  window.name = ''
})

//vue-router > 3.1 未处理promise catch问题
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => err)
}

export default router
